import React from "react";
import { Link } from "react-router-dom";
import {
  GraduationCap, FileCheck2, Sparkles, Home, Briefcase, Users2, Tag,
  Shield, Crown, ArrowRight, Check, Star, MapPin, HeartPulse,
  Globe2, Languages, BookOpen,
} from "lucide-react";
import { useSeo } from "@/lib/useSeo";

const FEATURES = [
  { icon: FileCheck2, title: "Administrative paperwork", desc: "CAF (housing aid), CROUS, French health insurance (Ameli), residence permit, tax, banking — 15 step-by-step guides." },
  { icon: Home, title: "Student housing", desc: "CROUS dorms, CAF (APL/ALS) housing subsidies, Action Logement, Visale guarantor. Search, apply, sign — all in one place." },
  { icon: Briefcase, title: "Jobs & work-study", desc: "Part-time jobs, internships, apprenticeships, babysitting. Curated platforms + French net-salary calculator." },
  { icon: Sparkles, title: "AI assistant", desc: "French-style résumé, cover letter and interview prep — powered by Claude Sonnet 4.5." },
  { icon: Tag, title: "Student discounts", desc: "SNCF rail card, cinema, gym, culture, festivals. All French student deals centralised." },
  { icon: Users2, title: "Local community", desc: "City-based forum: flatshare, sublets, advice, peer support — translatable to your language." },
  { icon: HeartPulse, title: "Well-being", desc: "Free psychological support (Santé Psy Étudiant), CROUS social workers, helplines, addictions." },
  { icon: Shield, title: "Security & GDPR", desc: "2FA, encryption, email verification, right to erasure. Your data stays in France." },
];

const TESTIMONIALS = [
  { name: "Léa, 21", role: "Law student · Lyon", quote: "I sorted out all my back-to-school paperwork in one afternoon. CAF and CROUS done — no stress.", stars: 5 },
  { name: "Yanis, 23", role: "MSc Computer Science · Paris", quote: "The CV generator is amazing. I got 3 interviews in 2 weeks with a cover letter written in 30 seconds.", stars: 5 },
  { name: "Sarah, 19", role: "BUT GEA · Bordeaux", quote: "The community helped me find a flatshare. I'd never seen the SNCF deals anywhere else.", stars: 5 },
];

const FAQ = [
  {
    q: "Is Student Hub really free?",
    a: "Yes. Every administrative guide, the community, progress tracking and the CV generator (with a free quota) are 100% free. Premium (€2.99/month) unlocks unlimited AI generation, advanced legal templates and early access to discounts.",
  },
  {
    q: "Do you support non-French speakers?",
    a: "Yes. The interface is in French but every community message (chat and forum) can be translated on demand into 8 languages — English, Spanish, Arabic, Chinese, German, Italian, Portuguese and French. Administrative guides use simple French and link to the official sources.",
  },
  {
    q: "Is my data secure?",
    a: "Yes. Password hashing (bcrypt), mandatory email verification, optional 2FA, HTTPS end-to-end encryption, hosted in France and fully GDPR-compliant. You can export or delete all your data at any time from your settings.",
  },
  {
    q: "Who is Student Hub for?",
    a: "Every student in France: high-schoolers transitioning to higher education, BUT, BTS, prep schools, universities, master's, PhDs, business and engineering schools. Whether you're French or an international/Erasmus student, this app is built for you.",
  },
  {
    q: "How does the AI assistant work?",
    a: "Powered by Claude Sonnet 4.5 (Anthropic), one of the most advanced LLMs in the world. Describe your background and the role you're targeting — it generates a French-style CV or cover letter (Europass-compatible).",
  },
  {
    q: "Can I cancel Premium at any time?",
    a: "Yes. Cancel in one click from your settings (Stripe customer portal). No commitment, no hidden fees. Access remains until the end of your paid period.",
  },
];

export default function LandingStudentsEN() {
  const canonical = "https://studenthubfrance.fr/en/students";
  const title = "Student Hub — French student admin, housing & jobs for Erasmus and international students";
  const description = "The all-in-one French student app for Erasmus and international students. CAF housing aid, CROUS, residence permit, jobs, AI-powered CV & cover letter. Community translated into 8 languages. Free, secure, made in France.";
  const ogImage = "https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=1200&q=85";

  const hreflangs = [
    { lang: "fr", url: "https://studenthubfrance.fr/landing/etudiants" },
    { lang: "en", url: "https://studenthubfrance.fr/en/students" },
    { lang: "x-default", url: "https://studenthubfrance.fr/landing/etudiants" },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    name: "Student Hub",
    url: "https://studenthubfrance.fr",
    description,
    applicationCategory: "EducationApplication",
    operatingSystem: "Web",
    inLanguage: "en",
    audience: {
      "@type": "EducationalAudience",
      educationalRole: "International student",
    },
    offers: [
      { "@type": "Offer", name: "Free", price: "0", priceCurrency: "EUR" },
      { "@type": "Offer", name: "Premium monthly", price: "2.99", priceCurrency: "EUR" },
    ],
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: "4.9",
      ratingCount: "127",
      bestRating: "5",
    },
    publisher: {
      "@type": "Organization",
      name: "Student Hub",
      url: "https://studenthubfrance.fr",
    },
  };

  const faqLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map(({ q, a }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text: a },
    })),
  };

  useSeo({ title, description, canonical, ogImage, jsonLd, hreflangs, lang: "en" });

  // Inject FAQ JSON-LD separately
  React.useEffect(() => {
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.text = JSON.stringify(faqLd);
    el.dataset.seoHook = "1";
    document.head.appendChild(el);
    return () => el.remove();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased" data-testid="landing-en-students">
      {/* Decorative background */}
      <div aria-hidden="true" className="absolute inset-0 -z-10 overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40rem] h-[40rem] rounded-full bg-violet-200/40 blur-3xl" />
        <div className="absolute top-[20%] right-[-15%] w-[40rem] h-[40rem] rounded-full bg-blue-200/40 blur-3xl" />
        <div className="absolute bottom-[-10%] left-[20%] w-[30rem] h-[30rem] rounded-full bg-fuchsia-200/30 blur-3xl" />
      </div>

      {/* HEADER */}
      <header className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-center justify-between">
        <Link to="/en/students" className="inline-flex items-center gap-2 group" data-testid="landing-en-logo">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-violet-600 flex items-center justify-center shadow-lg shadow-violet-600/20 group-hover:scale-105 transition-transform">
            <GraduationCap size={20} className="text-white" />
          </div>
          <span className="font-display font-bold text-lg">Student Hub</span>
        </Link>
        <nav className="flex items-center gap-3 text-sm">
          <Link to="/landing/etudiants" className="hidden sm:inline text-slate-500 hover:text-slate-900" data-testid="landing-en-fr-link">🇫🇷 Version française</Link>
          <Link to="/login" className="text-slate-600 hover:text-slate-900" data-testid="landing-en-signin">Sign in</Link>
          <Link
            to="/register"
            className="inline-flex items-center gap-1 px-4 py-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold"
            data-testid="landing-en-cta-header"
          >
            Get started <ArrowRight size={14} />
          </Link>
        </nav>
      </header>

      {/* HERO */}
      <main className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pb-20">
        <section className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center pt-10 sm:pt-20">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full bg-violet-50 border border-violet-200 px-3 py-1 mb-5">
              <Sparkles size={14} className="text-violet-600" />
              <span className="text-xs font-semibold text-violet-700 tracking-wide">Powered by Claude Sonnet 4.5 · Made in France 🇫🇷</span>
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-[1.05]">
              Studying in France? <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">We've got your back.</span>
            </h1>
            <p className="text-slate-600 mt-5 text-lg leading-relaxed max-w-xl">
              The French administration shouldn't feel like a maze. Student Hub centralises every step: CAF housing aid, CROUS dorms, residence permit, health insurance, bank account, jobs. Built for <strong>Erasmus and international students</strong>.
            </p>
            <div
              className="mt-5 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gradient-to-r from-emerald-50 to-blue-50 border border-emerald-200 text-xs font-medium text-emerald-800 shadow-sm"
              data-testid="hero-multilingue-badge"
            >
              <Globe2 size={14} aria-hidden="true" />
              <span className="font-semibold">Translatable community</span>
              <span className="text-emerald-700/80">· EN · FR · ES · AR · 中文 · DE · IT · PT</span>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white text-base font-semibold shadow-xl shadow-violet-600/30 transition-all hover:shadow-2xl"
                data-testid="hero-primary-cta"
              >
                Create my free account <ArrowRight size={16} />
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 hover:border-slate-400 text-slate-900 text-base font-medium bg-white transition-colors"
                data-testid="hero-secondary-cta"
              >
                I already have an account
              </Link>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-500">
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> 100% free tier</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> GDPR · Data stays in France</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> No ads, no trackers</span>
              <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-emerald-500" /> Delete your account in 1 click</span>
            </div>
          </div>
          <div className="relative">
            <img
              src="https://images.unsplash.com/photo-1627556704290-2b1f5853ff78?auto=format&fit=crop&w=900&q=85"
              alt="International students celebrating graduation in France — Student Hub app"
              className="w-full max-w-md mx-auto rounded-3xl shadow-2xl object-cover aspect-[4/5]"
              loading="eager"
            />
            <div className="hidden md:block absolute -bottom-6 -left-6 bg-white border border-slate-200 rounded-2xl shadow-xl px-4 py-3 max-w-xs">
              <div className="flex items-center gap-2 mb-1">
                {[1,2,3,4,5].map((s) => <Star key={s} size={14} className="fill-amber-400 text-amber-400" />)}
                <span className="text-xs font-semibold text-slate-700">4.9 / 5</span>
              </div>
              <p className="text-xs text-slate-600">"Everything in one place. No more juggling 8 different websites."</p>
            </div>
          </div>
        </section>

        {/* FEATURES grid */}
        <section className="mt-24" id="features">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">What's inside</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">8 pillars to succeed as a student in France</h2>
            <p className="text-slate-600 mt-3">Built for French and international students alike. GDPR-compliant. Hosted in France.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="features-grid">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <div key={f.title} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-violet-300 hover:shadow-lg transition-all">
                  <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-violet-600 text-white flex items-center justify-center mb-4 shadow-md shadow-violet-500/30">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-display font-semibold text-slate-900 text-base mb-1.5">{f.title}</h3>
                  <p className="text-sm text-slate-600 leading-relaxed">{f.desc}</p>
                </div>
              );
            })}
          </div>
        </section>

        {/* ERASMUS / INTERNATIONAL section */}
        <section className="mt-24" id="erasmus" aria-labelledby="erasmus-title">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-emerald-50 via-blue-50 to-violet-50 border border-emerald-100 p-8 sm:p-12">
            <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-xs font-semibold text-emerald-800 shadow-sm">
                  <Globe2 size={14} className="text-emerald-600" />
                  Erasmus · International students
                </div>
                <h2 id="erasmus-title" className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-4 leading-tight">
                  New to France? Student Hub speaks your language.
                </h2>
                <p className="text-slate-700 mt-4 leading-relaxed">
                  Erasmus, exchange, internship, or full master's degree in France — French paperwork can feel overwhelming. Student Hub gives you every step (CAF, CROUS, Préfecture residence permit, French social security) with clear guides, and connects you to a friendly student community — translatable on demand into <strong>8 languages</strong>.
                </p>
                <ul className="mt-6 space-y-3" data-testid="erasmus-bullets">
                  <li className="flex items-start gap-3" data-testid="er-bullet-translate">
                    <div className="w-9 h-9 rounded-xl bg-white border border-emerald-200 flex items-center justify-center shrink-0 shadow-sm">
                      <Languages size={16} className="text-emerald-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">One-click translation of every community message</p>
                      <p className="text-sm text-slate-600 mt-0.5">Anonymous chat and city forum — translatable on demand into EN, FR, ES, AR, 中文, DE, IT, PT. No message will ever feel out of reach.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="er-bullet-charter">
                    <div className="w-9 h-9 rounded-xl bg-white border border-blue-200 flex items-center justify-center shrink-0 shadow-sm">
                      <Users2 size={16} className="text-blue-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Erasmus-friendly kindness charter</p>
                      <p className="text-sm text-slate-600 mt-0.5">Zero judgment, zero discrimination. Anti-harassment moderation (3 reports = message auto-hidden) and free mental-health helplines. You're welcome here.</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3" data-testid="er-bullet-guides">
                    <div className="w-9 h-9 rounded-xl bg-white border border-violet-200 flex items-center justify-center shrink-0 shadow-sm">
                      <BookOpen size={16} className="text-violet-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900 text-sm">Step-by-step guides for non-French speakers</p>
                      <p className="text-sm text-slate-600 mt-0.5">CAF housing aid, CROUS, residence permit (titre de séjour), Ameli health insurance, French bank account, Visale guarantor. Plain French + official links.</p>
                    </div>
                  </li>
                </ul>
                <div className="mt-7 flex flex-wrap items-center gap-3">
                  <Link
                    to="/register"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold transition-colors shadow-md"
                    data-testid="er-cta-register"
                  >
                    Get started — it's free <ArrowRight size={14} />
                  </Link>
                  <span className="text-xs text-slate-500">Free · No credit card required</span>
                </div>
              </div>
              <div className="lg:col-span-5">
                <div className="relative rounded-2xl bg-white border border-slate-200 shadow-xl p-5 max-w-sm mx-auto">
                  <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-400 to-blue-500 flex items-center justify-center text-white text-sm font-bold">M</div>
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-slate-900 truncate">Maria · Erasmus Spain</p>
                      <p className="text-[10px] text-slate-500">MA1 · Bordeaux · 2 min ago</p>
                    </div>
                    <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      🇬🇧 Translated
                    </span>
                  </div>
                  <p className="text-sm text-slate-800 leading-relaxed">
                    Hi everyone! I just got my student visa. Do I need to register at the Préfecture once I arrive, or only after the first month? Any tips from someone who did it recently?
                  </p>
                  <p className="text-xs text-slate-400 italic mt-2">→ Original Spanish auto-translated</p>
                  <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100 text-xs">
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-violet-50 text-violet-700 font-medium">❤️ 18</span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 font-medium">🤗 7</span>
                    <span className="text-slate-400 ml-auto">Student Hub community</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="mt-24" id="testimonials">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Loved by students</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Students who saved hours of admin time</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="testimonials-grid">
            {TESTIMONIALS.map((t) => (
              <figure key={t.name} className="rounded-2xl border border-slate-200 bg-white p-6 relative">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.stars }).map((_, i) => (
                    <Star key={i} size={14} className="fill-amber-400 text-amber-400" />
                  ))}
                </div>
                <blockquote className="text-slate-700 text-sm leading-relaxed">"{t.quote}"</blockquote>
                <figcaption className="mt-4 pt-4 border-t border-slate-100">
                  <p className="font-semibold text-slate-900 text-sm">{t.name}</p>
                  <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                    <MapPin size={11} /> {t.role}
                  </p>
                </figcaption>
              </figure>
            ))}
          </div>
        </section>

        {/* PRICING */}
        <section className="mt-24" id="pricing">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">Simple pricing</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Free forever. Premium if you want more.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 max-w-3xl mx-auto" data-testid="pricing-grid">
            <div className="rounded-2xl border-2 border-slate-200 bg-white p-7">
              <p className="text-sm font-semibold text-slate-700">Free</p>
              <p className="font-display text-4xl font-bold text-slate-900 mt-2">€0<span className="text-base font-normal text-slate-500">/month</span></p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> All admin guides</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> City-based community</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> Secure document storage</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-emerald-500 shrink-0 mt-0.5" /> AI: 5 generations / month</li>
              </ul>
              <Link to="/register" className="mt-6 block text-center px-5 py-3 rounded-full border border-slate-300 hover:border-slate-400 font-semibold text-sm">
                Start free
              </Link>
            </div>
            <div className="rounded-2xl border-2 border-violet-400 bg-gradient-to-br from-white via-violet-50/30 to-blue-50/30 p-7 relative">
              <span className="absolute -top-3 right-6 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white text-[10px] font-bold tracking-wide uppercase">
                <Crown size={11} /> Recommended
              </span>
              <p className="text-sm font-semibold text-violet-700">Premium</p>
              <p className="font-display text-4xl font-bold text-slate-900 mt-2">€2.99<span className="text-base font-normal text-slate-500">/month</span></p>
              <p className="text-xs text-emerald-700 mt-1">7-day free trial · Cancel anytime</p>
              <ul className="mt-5 space-y-2 text-sm text-slate-700">
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Everything in Free</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Unlimited AI (CV, cover letters, interview prep)</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Legal templates (lease, appeal letters…)</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Early access to discounts</li>
                <li className="flex items-start gap-2"><Check size={16} className="text-violet-500 shrink-0 mt-0.5" /> Priority support</li>
              </ul>
              <Link to="/register" className="mt-6 block text-center px-5 py-3 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white hover:from-violet-700 hover:to-blue-700 font-semibold text-sm shadow-md">
                Start 7-day trial
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="mt-24" id="faq">
          <div className="max-w-2xl mx-auto text-center mb-10">
            <p className="text-xs uppercase tracking-[0.25em] text-violet-600 font-semibold mb-3">FAQ</p>
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900">Frequently asked questions</h2>
          </div>
          <div className="max-w-3xl mx-auto space-y-3" data-testid="faq-list">
            {FAQ.map((f, i) => (
              <details key={i} className="group rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-violet-300 transition-colors">
                <summary className="font-semibold text-slate-900 cursor-pointer flex items-center justify-between">
                  {f.q}
                  <span className="text-violet-500 transition-transform group-open:rotate-180">⌄</span>
                </summary>
                <p className="text-sm text-slate-600 mt-3 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        {/* Final CTA */}
        <section className="mt-24 text-center">
          <div className="rounded-3xl bg-gradient-to-r from-blue-600 to-violet-600 px-8 sm:px-16 py-14 text-white">
            <h2 className="font-display text-3xl sm:text-4xl font-bold">Get started in less than 60 seconds</h2>
            <p className="mt-3 text-blue-50 max-w-xl mx-auto">Free account · No credit card · Cancel any time. Built for Erasmus and international students arriving in France.</p>
            <Link to="/register" className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-white text-slate-900 font-semibold hover:bg-slate-100 transition-colors shadow-xl">
              Create my free account <ArrowRight size={16} />
            </Link>
          </div>
        </section>
      </main>

      {/* FOOTER */}
      <footer className="border-t border-slate-100 py-10 mt-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-slate-500">
          <p>© 2026 Student Hub · All rights reserved · Made in France 🇫🇷</p>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <Link to="/landing/etudiants" className="hover:text-slate-900">🇫🇷 Version française</Link>
            <Link to="/legal/cgu" className="hover:text-slate-900">Terms</Link>
            <Link to="/legal/confidentialite" className="hover:text-slate-900">Privacy</Link>
            <Link to="/legal/mentions" className="hover:text-slate-900">Legal notice</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
